/**
 * The Mac and Windows apps (docs/adr/0022-desktop-apps.md) open this web app and expose one bridge,
 * `window.chapsiDesktop`. In a browser it is absent and these calls do nothing.
 */
interface DesktopBridge {
  platform: string;
  setBadge: (count: number) => void;
  /** Shows and focuses the window (a notification was clicked while it was hidden or minimised). */
  focus?: () => void;
  /** Keep running in the menu bar or tray when the window is closed (docs/adr/0048). */
  getRunInBackground?: () => Promise<boolean>;
  setRunInBackground?: (on: boolean) => void;
  /**
   * Continue with Google or Microsoft (docs/adr/0081): the shell hands back what reached its own address
   * (`chapsi-desktop://social-callback`): a single-use code, or one of our reason codes. Returns an unsubscribe.
   * Shells from before this have none, and the sign-in screen then shows no provider buttons.
   */
  onSocialReturn?: (cb: (back: { code?: string; error?: string; email?: string }) => void) => () => void;
  /** My day's desktop sticky notes (docs/adr/0105). Shells from before this have none, and no pin button shows. */
  notes?: DesktopNotesBridge;
}

/** What the shell offers for desktop notes: the main window syncs and hears Open in Chapsi; a note window reads its note. */
export interface DesktopNotesBridge {
  sync: (scope: string | null, notes: unknown[]) => void;
  onOpen: (cb: (noteId: string) => void) => () => void;
  get: () => Promise<unknown>;
  onData: (cb: (note: unknown) => void) => () => void;
  close: () => void;
  openInApp: () => void;
}

const bridge = (): DesktopBridge | undefined => (typeof window === 'undefined' ? undefined : (window as Window & { chapsiDesktop?: DesktopBridge }).chapsiDesktop);

export const isDesktopApp = (): boolean => !!bridge();

/** The count on the Dock (Mac) or taskbar (Windows): unread mentions and direct messages, as the rail's Chat badge (docs/adr/0095). */
export function setDesktopBadge(count: number): void {
  bridge()?.setBadge(Math.max(0, Math.floor(count)));
}

/** Brings the window forward: the desktop app's own call, else the browser's. */
export function focusApp(): void {
  const b = bridge();
  if (b?.focus) b.focus();
  else window.focus();
}

/** Whether this desktop app can keep running in the menu bar or tray (older shells cannot). */
export const canRunInBackground = (): boolean => !!bridge()?.setRunInBackground;

export async function getRunInBackground(): Promise<boolean> {
  try {
    return (await bridge()?.getRunInBackground?.()) ?? false;
  } catch {
    return false;
  }
}

export function setRunInBackground(on: boolean): void {
  bridge()?.setRunInBackground?.(on);
}

/** Whether this desktop shell can bring a Google or Microsoft sign-in back to the window (older shells cannot). */
export const canReturnFromProvider = (): boolean => !!bridge()?.onSocialReturn;

/** Listens for the shell's provider return; a no-op outside a desktop shell that supports it. */
export function onProviderReturn(cb: (back: { code?: string; error?: string; email?: string }) => void): () => void {
  return bridge()?.onSocialReturn?.(cb) ?? (() => undefined);
}

/** Whether this is the desktop app with sticky notes (docs/adr/0105): only then does My day offer "Pin to desktop". */
export const canPinNotes = (): boolean => {
  const n = bridge()?.notes;
  return !!n && typeof n.sync === 'function';
};

/** The desktop notes bridge, or undefined in a browser or an older shell. */
export const notesBridge = (): DesktopNotesBridge | undefined => (canPinNotes() ? bridge()!.notes : undefined);
